/**
 * PATH: packages/email-hub/src/feature/reader-header/notices/NoticeSummary.tsx
 * FILE_ID: EMAILHUB-READERHEADER-NOTICES-SUMMARY-001
 * PURPOSE: Render the single at-rest notice line: highest severity + "+N notices".
 * ROLE IN ARCHITECTURE: Phase 1 placeholder for the Phase 6 Notice Center (plan D269-16).
 * MAIN EXPORTS: ReaderNoticeSummary.
 * NON-RESPONSIBILITIES: Notice actions (accept/decline/retry) ship with the
 *   Phase 6 Notice Center; this line only guarantees no information is lost.
 * NOTES FOR FUTURE AI: Ordering comes from noticeRegistry severity — never
 *   reorder locally. Replace this leaf with the Notice Center, keep the testid.
 */
import { AlertTriangle, Bell, CalendarClock, Info, Paperclip } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { ReaderNotice, ReaderNoticeKind } from '../model/types'
import { selectAtRestNotices } from './noticeRegistry'

const NOTICE_ICONS: Record<ReaderNoticeKind, typeof Info> = {
  error: AlertTriangle,
  provider: AlertTriangle,
  ping: Bell,
  calendar: CalendarClock,
  attachments: Paperclip,
  info: Info,
}

export function ReaderNoticeSummary({
  notices,
  onOpen,
}: {
  notices: readonly ReaderNotice[]
  /** Expands the Notice Center detail panel (change #269 Phase 6 unification). */
  onOpen?: () => void
}) {
  const atRest = selectAtRestNotices(notices)
  const lead = atRest[0] ?? null
  if (!lead) return null
  // Bug: this used to be `sortReaderNotices(notices).length - 1`, which
  // counted resolved/acknowledged notices that are already excluded from
  // the at-rest list — harmless with only two kinds, wrong the moment more
  // land (change #269 provider/ping/attachments). Count off the SAME
  // at-rest list the lead came from.
  const remaining = atRest.length - 1
  const Icon = NOTICE_ICONS[lead.kind]

  return (
    <div
      className={cn(
        'flex min-h-7 items-center gap-2 border-b border-border/50 px-3 py-1 text-[11px]',
        lead.kind === 'error' || lead.kind === 'provider'
          ? 'bg-destructive/5 text-destructive'
          : 'bg-muted/40 text-muted-foreground',
      )}
      data-testid="reader-notice-summary"
      data-notice-kind={lead.kind}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{lead.label}</span>
      {lead.actionLabel && onOpen && (
        <button
          type="button"
          onClick={onOpen}
          className="shrink-0 rounded px-1.5 py-0.5 font-medium text-foreground hover:bg-accent"
        >
          {lead.actionLabel}
        </button>
      )}
      {remaining > 0 &&
        (onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums hover:bg-accent"
            data-testid="reader-notice-more-count"
          >
            +{remaining} notice{remaining === 1 ? '' : 's'}
          </button>
        ) : (
          <span
            className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums"
            data-testid="reader-notice-more-count"
          >
            +{remaining} notice{remaining === 1 ? '' : 's'}
          </span>
        ))}
    </div>
  )
}
